import { useState } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/d6td1dlm9vk3c28i5gp0";

export default function LeadForm() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-24 bg-[#0f2018]" id="estimate">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Pre-form CTA headline */}
        <div className="text-center mb-14">
          <span className="text-[#e8632a] font-bold text-sm uppercase tracking-[3px] block mb-4">Get Started</span>
          <h2 className="text-4xl lg:text-5xl font-black text-white mb-4">
            Your Free Estimate Is<br />
            <span className="text-[#e8632a]">One Step Away</span>
          </h2>
          <p className="text-white/50 text-base max-w-lg mx-auto">
            Fill out the form or call us directly. No obligation, no high-pressure sales — just honest, professional tree service.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Form */}
          <div className="lg:col-span-3 bg-white rounded-3xl p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#e8632a] rounded-xl">
                <i className="ri-file-list-3-fill text-white text-lg"></i>
              </div>
              <div>
                <h3 className="font-black text-[#1e1e1e] text-xl">Request Free Estimate</h3>
                <p className="text-gray-400 text-xs">We respond within 2 hours · No obligation</p>
              </div>
            </div>

            {status === "success" ? (
              <div className="flex flex-col items-center justify-center py-14 text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-[#1a3a2e]/10 rounded-full mb-4">
                  <i className="ri-check-double-line text-4xl text-[#1a3a2e]"></i>
                </div>
                <h3 className="font-black text-[#1e1e1e] text-2xl mb-3">Request Sent!</h3>
                <p className="text-gray-500 text-sm max-w-xs">We'll be in touch within 2 hours. For urgent needs, call us directly at (540) 776-4488.</p>
                <a href="tel:+15407764488" className="mt-4 flex items-center gap-2 text-[#e8632a] font-bold cursor-pointer hover:underline">
                  <i className="ri-phone-fill"></i>(540) 776-4488
                </a>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-black text-[#1e1e1e] uppercase tracking-wide mb-1.5">Full Name *</label>
                    <input type="text" name="name" required placeholder="John Smith" className="w-full h-12 px-4 rounded-xl border-2 border-gray-100 bg-[#f8f6f3] text-sm focus:outline-none focus:border-[#e8632a] focus:bg-white transition-all" />
                  </div>
                  <div>
                    <label className="block text-xs font-black text-[#1e1e1e] uppercase tracking-wide mb-1.5">Phone Number *</label>
                    <input type="tel" name="phone" required placeholder="(540) 000-0000" className="w-full h-12 px-4 rounded-xl border-2 border-gray-100 bg-[#f8f6f3] text-sm focus:outline-none focus:border-[#e8632a] focus:bg-white transition-all" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-black text-[#1e1e1e] uppercase tracking-wide mb-1.5">Email Address *</label>
                  <input type="email" name="email" required placeholder="your@email.com" className="w-full h-12 px-4 rounded-xl border-2 border-gray-100 bg-[#f8f6f3] text-sm focus:outline-none focus:border-[#e8632a] focus:bg-white transition-all" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-black text-[#1e1e1e] uppercase tracking-wide mb-1.5">Service Needed *</label>
                    <select name="service" required className="w-full h-12 px-4 rounded-xl border-2 border-gray-100 bg-[#f8f6f3] text-sm focus:outline-none focus:border-[#e8632a] focus:bg-white transition-all cursor-pointer">
                      <option value="">Select service...</option>
                      <option>Tree Removal</option>
                      <option>Tree Trimming</option>
                      <option>Stump Grinding</option>
                      <option>Emergency Service</option>
                      <option>Multiple Services</option>
                      <option>Not Sure / Need Assessment</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-black text-[#1e1e1e] uppercase tracking-wide mb-1.5">Your City / Zip *</label>
                    <input type="text" name="location" required placeholder="Fredericksburg, VA" className="w-full h-12 px-4 rounded-xl border-2 border-gray-100 bg-[#f8f6f3] text-sm focus:outline-none focus:border-[#e8632a] focus:bg-white transition-all" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-black text-[#1e1e1e] uppercase tracking-wide mb-1.5">Describe Your Project</label>
                  <textarea name="message" rows={3} maxLength={500} placeholder="Tell us about the trees — size, location, any concerns..." className="w-full px-4 py-3 rounded-xl border-2 border-gray-100 bg-[#f8f6f3] text-sm focus:outline-none focus:border-[#e8632a] focus:bg-white transition-all resize-none"></textarea>
                </div>
                {status === "error" && (
                  <p className="text-red-500 text-sm">Something went wrong. Please call (540) 776-4488.</p>
                )}
                <button type="submit" disabled={loading} className="w-full h-14 bg-[#e8632a] text-white font-black text-lg rounded-xl uppercase tracking-wide hover:bg-[#d0541e] transition-all cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2 whitespace-nowrap">
                  {loading ? <><i className="ri-loader-4-line animate-spin"></i>Sending...</> : <><i className="ri-send-plane-fill"></i>Request Free Estimate</>}
                </button>
                <div className="flex flex-wrap items-center justify-center gap-4 pt-1">
                  {["Free & No Obligation", "Fast Response", "Licensed & Insured"].map((t) => (
                    <span key={t} className="flex items-center gap-1 text-gray-400 text-xs">
                      <i className="ri-check-line text-[#1a3a2e]"></i>{t}
                    </span>
                  ))}
                </div>
              </form>
            )}
          </div>

          {/* Right: contact sidebar */}
          <div className="lg:col-span-2 flex flex-col justify-center">
            <h3 className="text-white font-black text-2xl mb-2">Prefer to Call?</h3>
            <p className="text-white/50 text-sm mb-8 leading-relaxed">
              Our team is available Monday–Saturday 7am–7pm. Emergency service available 24/7.
            </p>

            <a href="tel:+15407764488" className="flex items-center gap-4 group cursor-pointer mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#e8632a] rounded-2xl flex-shrink-0">
                <i className="ri-phone-fill text-white text-3xl"></i>
              </div>
              <div>
                <p className="text-white/50 text-xs uppercase tracking-widest font-bold mb-1">Call Us Now</p>
                <p className="text-white font-black text-3xl group-hover:text-[#e8632a] transition-colors tracking-tight">(540) 776-4488</p>
              </div>
            </a>

            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl flex-shrink-0">
                  <i className="ri-mail-fill text-[#e8632a]"></i>
                </div>
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wide">Email</p>
                  <a href="mailto:info@diversifiedtreeservices.com" className="text-white text-sm font-semibold hover:text-[#e8632a] transition-colors">info@diversifiedtreeservices.com</a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl flex-shrink-0">
                  <i className="ri-time-fill text-[#e8632a]"></i>
                </div>
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wide">Hours</p>
                  <p className="text-white text-sm font-semibold">Mon–Sat: 7am – 7pm</p>
                  <p className="text-white/40 text-xs">Emergency: 24/7 Available</p>
                </div>
              </div>
            </div>

            {/* Emergency badge */}
            <div className="bg-[#e8632a]/10 border border-[#e8632a]/25 rounded-2xl p-5 flex items-center gap-4">
              <i className="ri-alarm-warning-fill text-[#e8632a] text-3xl flex-shrink-0"></i>
              <div>
                <p className="text-white font-black text-sm mb-1">24/7 Emergency Response</p>
                <p className="text-white/50 text-xs">Storm damage? Fallen tree? Call immediately for rapid emergency response across Northern Virginia.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
